import h from 'hyperscript';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import { applyOpacityFactorToColor, COLORS } from 'cadenza/utils/colors/colors';
import { DEVICE_PIXEL_RATIO } from 'ol/has';
import { mediaXsOnly } from 'cadenza/utils/media';

const DEFAULT_WAYPOINT_MARKER_COLOR = COLORS.GRAY_08_OPAQUE;
const DEFAULT_WAYPOINT_LABEL_COLOR = COLORS.WHITE_07;
const SELECTED_WAYPOINT_STYLE = {
  labelColor: COLORS.WHITE_09
};

const ERROR_WAYPOINT_STYLE = {
  markerFillColor: COLORS.COLOR_ERROR,
  markerStrokeColor: COLORS.COLOR_ERROR_DARK,
  labelColor: COLORS.WHITE_09
};

const SAVED_DIRECTIONS_WAYPOINT_STYLE = {
  markerFillColor: COLORS.GRAY_06_OPAQUE,
  markerStrokeColor: COLORS.GRAY_04,
  labelColor: COLORS.WHITE_07
};

// The style for the isochrone waypoints is different due to the reduced opacity applied to the isochrone layers including the waypoints
const SAVED_ISOCHRONE_WAYPOINT_STYLE = {
  markerFillColor: '#000000',
  markerStrokeColor: '#000000',
  labelColor: COLORS.WHITE_09
};

const MARKER_STROKE_SIZE_PX = 1;
const MARKER_DIAMETER_PX = mediaXsOnly.matches ? 33 : 18;
const MAX_FONT_SIZE = mediaXsOnly.matches ? 20 : 12;

export function createPointFeatureStyle (label, {
  markerFillColor = DEFAULT_WAYPOINT_MARKER_COLOR,
  markerStrokeColor = DEFAULT_WAYPOINT_MARKER_COLOR,
  labelColor = DEFAULT_WAYPOINT_LABEL_COLOR
} = {}) {
  const scaledMarkerDiameter = DEVICE_PIXEL_RATIO * MARKER_DIAMETER_PX;
  const borderWidth = DEVICE_PIXEL_RATIO * MARKER_STROKE_SIZE_PX;
  const maxFontSize = DEVICE_PIXEL_RATIO * MAX_FONT_SIZE;
  const iconCanvas = createMarkerCanvas(label, markerFillColor, markerStrokeColor, labelColor, scaledMarkerDiameter, borderWidth, maxFontSize, 0.59);

  return new Style({
    image: new Icon({
      img: iconCanvas,
      imgSize: [ iconCanvas.width, iconCanvas.height ],
      anchor: [ 0.5, 1 ],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      scale: 1 / DEVICE_PIXEL_RATIO
    })
  });
}

export function createSelectedPointFeatureStyle (label) {
  return createPointFeatureStyle(label, SELECTED_WAYPOINT_STYLE);
}

export function createErrorPointFeatureStyle (label) {
  return createPointFeatureStyle(label, ERROR_WAYPOINT_STYLE);
}

export function createSavedLayerPointFeatureStyle (label, opacity, waypointType) {
  let style = waypointType === 'isochrone' ? SAVED_ISOCHRONE_WAYPOINT_STYLE : SAVED_DIRECTIONS_WAYPOINT_STYLE;
  if (opacity < 1) {
    style = {
      ...style,
      markerFillColor: applyOpacityFactorToColor(style.markerFillColor, opacity),
      markerStrokeColor: applyOpacityFactorToColor(style.markerStrokeColor, opacity),
      labelColor: applyOpacityFactorToColor(style.labelColor, opacity)
    };
  }
  return createPointFeatureStyle(label, style);
}

/**
 * Draw the map marker icon into a canvas and return the canvas.
 *
 * @param {string} label - Label to use (should not be more than one or 2 letters)
 * @param {string} markerFillColor - Color to use for the marker shape fill
 * @param {string} markerStrokeColor - Color to use for the marker shape stroke
 * @param {string} labelColor - Color to use for the label
 * @param {number} diameter - Diameter of the circle to use to draw the shape
 * @param {number} borderWidth - Border width of the marker shape
 * @param {number} maxFontSize - Maximal label font size in pixels, the font size will calculated automatically, so the label fits into the marker, but not larger than this max
 * @param {number} pointiness - The "pointiness" of the shape, a value between 0 and 1 (those values not included).
 * 0 Would yield a circle (not pointy at all), and 1, a very pointy shape of infinite height.
 * Good values are between approximately 0.45 and 0.7.
 * @return {HTMLCanvasElement} the canvas with the marker
 */
function createMarkerCanvas (label, markerFillColor, markerStrokeColor, labelColor, diameter, borderWidth, maxFontSize, pointiness) {
  const radius = diameter / 2;
  const pointerAngle = (1 - pointiness) / 2 * Math.PI;
  const height = Math.round(radius + radius / Math.sin(pointerAngle));

  const canvas = h('canvas');
  const context = canvas.getContext('2d');
  canvas.width = diameter + borderWidth * 2;
  canvas.height = height + borderWidth * 2;

  context.beginPath();
  context.arc(radius + borderWidth, radius + borderWidth, radius, Math.PI - pointerAngle, pointerAngle);
  context.lineTo(radius + borderWidth, height + borderWidth);
  context.closePath();

  context.fillStyle = markerFillColor;
  context.fill();

  context.strokeStyle = markerStrokeColor;
  context.lineWidth = borderWidth;
  context.stroke();

  context.textAlign = 'center';
  context.fillStyle = labelColor;
  const [ font, textHeight ] = calculateMaxFont(label, '"Titillium Web"', 0.6 * diameter, maxFontSize);
  const textBottom = borderWidth + diameter - (diameter - textHeight) * 0.45;
  context.font = font;
  context.fillText(label, radius + borderWidth, textBottom);

  return canvas;
}

function calculateMaxFont (text, fontName, maxSize, maxFontSize) {
  const context = h('canvas').getContext('2d');
  let fontSize = Math.round(0.5 * maxSize);
  let font = '';
  let width = 0;
  let height = 0;

  while (width < maxSize && height < maxSize && fontSize <= maxFontSize) {
    font = `600 ${fontSize}px ${fontName}`;
    context.font = font;
    const metrics = context.measureText(text);
    width = metrics.width;
    height = determineHeight(metrics, fontSize);
    fontSize++;
  }
  return [ font, height ];
}

function determineHeight (metrics, fontSize) {
  if (metrics.fontBoundingBoxAscent) {
    return metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  }
  // IE11 does not support anything besides width, so we approximate it based on the font pixel size
  return 0.8 * fontSize;
}
